import {
  countPasswordStrengthCriteria,
  validateEmail,
  validatePasswordStrength,
  validateUsername,
} from 'domain/auth-rules';
import {
  CandidateRegistrationData,
  EmployerRegistrationData,
  PasswordStrengthColor,
} from './registrationTypes';

export function getPasswordStrength(password: string): {
  level: string;
  color: PasswordStrengthColor;
  value: number;
} {
  const strength = countPasswordStrengthCriteria(password);
  if (strength < 2) return { level: 'weak', color: 'error', value: 20 };
  if (strength < 4) return { level: 'medium', color: 'warning', value: 60 };
  return { level: 'strong', color: 'success', value: 100 };
}

export function validateCandidateRegistration(
  formData: CandidateRegistrationData
): Record<string, string> {
  const errors = validateAccountFields(formData);

  if (!formData.firstName.trim()) errors.firstName = 'First name is required';
  if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
  if (formData.phone && !/^[+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
    errors.phone = 'Please enter a valid phone number';
  }

  return errors;
}

export function validateEmployerRegistration(
  formData: EmployerRegistrationData
): Record<string, string> {
  const errors = validateAccountFields(formData);

  if (!formData.companyName.trim()) errors.companyName = 'Company name is required';
  if (!formData.industry) errors.industry = 'Industry is required';
  if (!formData.companySize) errors.companySize = 'Company size is required';
  if (!formData.companyDescription.trim()) {
    errors.companyDescription = 'Company description is required';
  } else if (formData.companyDescription.length < 50) {
    errors.companyDescription = 'Company description must be at least 50 characters';
  }

  if (formData.websiteUrl) {
    try {
      new URL(formData.websiteUrl);
    } catch {
      errors.websiteUrl = 'Please enter a valid website URL';
    }
  }

  return errors;
}

function validateAccountFields(formData: {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!formData.email) {
    errors.email = 'Email is required';
  } else if (!validateEmail(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!formData.username.trim()) {
    errors.username = 'Username is required';
  } else {
    const username = validateUsername(formData.username);
    if (!username.isValid) errors.username = username.issues.join('; ');
  }

  if (!formData.password) {
    errors.password = 'Password is required';
  } else {
    const password = validatePasswordStrength(formData.password);
    if (!password.isValid) errors.password = password.issues.join('; ');
  }

  if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  return errors;
}
